import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Crown, ChevronLeft, Loader2, Tag, Sparkles, Trophy, Palette, BarChart3, Users, ImageIcon } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { trackSubscribe } from '@/lib/metaPixel';
import { CheckoutDialog } from '@/components/billing/CheckoutDialog';
import SEOHead from '@/components/SEOHead';
import { SiteFooter } from '@/components/landing/SiteFooter';
import { softwareApplicationSchema, breadcrumbSchema } from '@/lib/seoSchemas';
import { OfficialsAddonPricingCard } from '@/components/officials/OfficialsAddonPricingCard';
import { BundlePlanCard, useBundleOffer } from '@/components/pricing/BundlePlanCard';
import { FreePlanCard } from '@/components/pricing/FreePlanCard';
import { ProPlanCard } from '@/components/pricing/ProPlanCard';
import { PricingReassurance } from '@/components/pricing/PricingReassurance';
import { PlanComparison } from '@/components/pricing/PlanComparison';
import { PricingFaq } from '@/components/pricing/PricingFaq';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading: authLoading, isPro, applyCoupon, checkAuth } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  // Billing-cycle toggle for the Pro card. Defaults to 'annual' because
  // that's the plan we want to sell — the toggle acts as a soft anchor
  // toward the yearly commit while keeping monthly one click away.
  const [billingCycle, setBillingCycle] = useState('annual');
  const bundleOffer = useBundleOffer();
  const freePlan = plans.find((p) => p.id === 'free');
  const proMonthly = plans.find((p) => p.id === 'pro_monthly');
  const proAnnual = plans.find((p) => p.id === 'pro_annual');
  const activePro = billingCycle === 'annual' ? proAnnual : proMonthly;
  useEffect(() => { if (user?.subscription === 'pro_monthly') setBillingCycle('monthly'); }, [user?.subscription]);
  // Celebration modal shown right after Pro activates — via coupon,
  // PayPal one-time capture, OR the redirect back from a PayPal recurring
  // subscription approval. `mode='trial'` prints "Your Pro trial is live
  // for N days" (coupon path); `mode='paid'` prints "Welcome to Pro!"
  // with the next renewal date (PayPal path).
  const [proCelebration, setProCelebration] = useState({
    open: false, mode: 'trial', days: 0, expires: null,
  });
  const proPlansRef = useRef(null);

  // Shared helper — every Pro-activation success path funnels through here
  // so the same celebration UI fires regardless of whether the user paid
  // or redeemed a coupon.
  const launchProCelebration = useCallback((expiresIso, mode = 'trial') => {
    let days = 0;
    let expires = null;
    try {
      if (expiresIso) {
        expires = new Date(expiresIso);
        const now = new Date();
        days = Math.max(1, Math.round((expires - now) / (1000 * 60 * 60 * 24)));
      }
    } catch { /* leave defaults */ }
    setProCelebration({ open: true, mode, days, expires });
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchPayPalClientId();
    
    // Handle PayPal return from recurring subscription approval
    const success = searchParams.get('success');
    const subId = searchParams.get('subscription_id');
    const plan = searchParams.get('plan');
    if (success === 'true') {
      if (subId) {
        // Activate the recurring subscription
        const token = localStorage.getItem('session_token');
        if (token) {
          fetch(`${API_URL}/api/paypal/capture-subscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ subscription_id: subId })
          }).then(r => r.json()).then(data => {
            if (data.success) {
              checkAuth();
              // Meta Pixel: fire Subscribe for the redirect-flow too so
              // we don't miss conversions that complete via PayPal's
              // hosted approval page (as opposed to the embedded flow
              // which fires it in onApprove below).
              const planAmount = plan === 'pro_annual' ? 199
                              : plan === 'pro_monthly' ? 24
                              : undefined;
              trackSubscribe({ value: planAmount, currency: 'USD', planId: plan });
              // Fire the shared Pro celebration for the paid subscription
              // flow. `expires_at` here is the next renewal date, and the
              // "paid" mode swaps trial-days copy for a Welcome-to-Pro
              // message with the renewal reminder.
              launchProCelebration(data.expires_at, 'paid');
            } else {
              toast.error(data.detail || 'Subscription activation issue - please contact support');
            }
          }).catch(() => {
            toast.success('Payment received! Your subscription is being activated.');
            checkAuth();
          });
        }
      } else {
        // Rare fallback — success=true came back without a subscription_id
        // (older PayPal SDK responses). Refresh auth and still show the
        // celebration since Pro just activated server-side.
        checkAuth().then(() => {
          launchProCelebration(null, 'paid');
        });
      }
    } else if (searchParams.get('canceled') === 'true') {
      toast.info('Payment was canceled.');
    }
  }, [searchParams, checkAuth, launchProCelebration]);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/plans`);
      const data = await response.json();
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayPalClientId = async () => {
    try {
      const response = await fetch(`${API_URL}/api/paypal/client-id`);
      const data = await response.json();
      setPaypalClientId(data.client_id);
    } catch (error) {
      console.error('Failed to fetch PayPal client ID:', error);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Please login to apply a coupon');
      navigate('/login');
      return;
    }

    setApplyingCoupon(true);
    try {
      const result = await applyCoupon(couponCode.trim());
      setCouponCode('');
      launchProCelebration(result.expires_at, 'trial');
    } catch (error) {
      toast.error(error.message || 'Invalid coupon code');
    } finally {
      setApplyingCoupon(false);
    }
  };

  // Close the celebration and scroll to the Pro plans grid so users can
  // see the full feature checklist that just unlocked. Uses smooth
  // scrolling with a header offset so the plans don't slide under the
  // sticky top bar.
  const handleExploreProFeatures = () => {
    setProCelebration({ open: false, mode: 'trial', days: 0, expires: null });
    // Defer the scroll so the modal has time to unmount and the grid
    // returns to its natural document offset.
    setTimeout(() => {
      const el = proPlansRef.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }, 120);
  };

  const handleSelectPlan = (planId) => {
    if (authLoading) return;
    if (!isAuthenticated) {
      toast.info('Please login to subscribe');
      navigate('/login');
      return;
    }

    if (planId === 'free') {
      navigate('/');
      return;
    }

    // Checkout dialog: card (Plug'n Pay) by default, PayPal falls back to the inline PayPal buttons.
    setSelectedPlan(null);
    setCheckoutPlan(planId);
  };

  const createPayPalOrder = async (planId) => {
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/api/paypal/create-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ plan_id: planId })
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Failed to create subscription');
    }
    
    // For recurring subscriptions, redirect to PayPal approval page
    if (data.approval_url) {
      window.location.href = data.approval_url;
      return data.subscription_id;
    }
    return data.order_id || data.subscription_id;
  };

  const capturePayPalOrder = async (orderId) => {
    const token = localStorage.getItem('session_token');
    const response = await fetch(`${API_URL}/api/paypal/capture-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ subscription_id: orderId, order_id: orderId })
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Failed to activate subscription');
    }
    
    return data;
  };

  const paypalBlock = activePro && (
                            <div className="space-y-3">
                              <PayPalScriptProvider options={{
                                "client-id": paypalClientId,
                                currency: "USD"
                              }}>
                                <PayPalButtons
                                  style={{ layout: "vertical", shape: "rect" }}
                                  createOrder={async () => {
                                    try {
                                      setProcessingPayment(true);
                                      return await createPayPalOrder(activePro.id);
                                    } catch (error) {
                                      toast.error(error.message);
                                      setProcessingPayment(false);
                                      throw error;
                                    }
                                  }}
                                  onApprove={async (data) => {
                                    try {
                                      const result = await capturePayPalOrder(data.orderID);
                                      await checkAuth();
                                      setSelectedPlan(null);
                                      // Meta Pixel: fire Subscribe with
                                      // value + currency so Facebook can
                                      // optimize campaigns toward revenue,
                                      // not just event counts. Fires once
                                      // per successful PayPal capture.
                                      trackSubscribe({
                                        value: activePro.price,
                                        currency: 'USD',
                                        planId: activePro.id,
                                      });
                                      launchProCelebration(result.expires_at, 'paid');
                                    } catch (error) {
                                      toast.error(error.message);
                                    } finally {
                                      setProcessingPayment(false);
                                    }
                                  }}
                                  onCancel={() => {
                                    setProcessingPayment(false);
                                    toast.info('Payment canceled');
                                  }}
                                  onError={(err) => {
                                    setProcessingPayment(false);
                                    toast.error('Payment failed. Please try again.');
                                    console.error('PayPal error:', err);
                                  }}
                                />
                              </PayPalScriptProvider>
                              <Button
                                variant="ghost"
                                className="w-full"
                                onClick={() => setSelectedPlan(null)}
                              >
                                Cancel
                              </Button>
                            </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Pricing | Schedule Guru — Free & Pro Tournament Scheduling Plans"
        description="Schedule Guru pricing: Free tier covers round robin scheduling for up to 8 teams. Pro at $24 USD/mo (or $199 USD/year — save 31%) unlocks every bracket format, live multi-sport stat tracking, AI schedule + box-score import, and season-long analytics."
        path="/pricing"
        type="website"
        jsonLd={[
          softwareApplicationSchema(),
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Pricing', url: '/pricing' },
          ]),
        ]}
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <ChevronLeft className="w-5 h-5 text-slate-600" />
              <span className="text-slate-600">Back</span>
            </Link>
            <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
              PRICING
            </h1>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600 mb-3">Pricing</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-slate-900 leading-[0.95]" style={{ fontFamily: 'Barlow Condensed' }} data-testid="pricing-headline">
            Simple Pricing. <span className="text-[#00247D]">Powerful League Management.</span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto mt-4" data-testid="pricing-subheadline">
            From your first tournament to managing an entire sports association, choose the plan that works for you.
          </p>
        </div>

        {/* Current Subscription Status */}
        {isAuthenticated && (
          <div className="mb-8 p-4 bg-[#00247D]/5 border border-[#00247D]/20 rounded-lg text-center">
            <p className="text-slate-700">
              Current plan: <span className="font-bold text-[#00247D]">{isPro ? 'Pro' : 'Free'}</span>
              {user?.subscription_expires && (
                <span className="text-sm text-slate-500 ml-2">
                  (expires {new Date(user.subscription_expires).toLocaleDateString()})
                </span>
              )}
            </p>
          </div>
        )}

        {/* Plans */}
        {loading || !freePlan || !proMonthly || !proAnnual ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
          </div>
        ) : (
          <section className="mb-14" data-testid="pricing-section">
            <div ref={proPlansRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8 items-stretch max-w-7xl mx-auto pt-4" data-testid="plans-grid">
              <FreePlanCard plan={freePlan} onStart={() => navigate(isAuthenticated ? '/' : '/login?mode=register')} />
              <ProPlanCard monthly={proMonthly} annual={proAnnual} cycle={billingCycle} onCycle={setBillingCycle} isPro={isPro} currentPlan={user?.subscription}
                onSelect={handleSelectPlan} paypal={selectedPlan === activePro.id && paypalClientId ? paypalBlock : null} />
              <BundlePlanCard />
            </div>
            <PricingReassurance />
          </section>
        )}

        <div id="officials-addon"><OfficialsAddonPricingCard /></div>

        {proMonthly && proAnnual && <PlanComparison pro={{ monthly: proMonthly.price, annual: proAnnual.price }} offer={bundleOffer} />}

        {/* Coupon Section */}
        <Card className="max-w-md mx-auto border-slate-200">
          <CardHeader className="text-center">
            <Tag className="w-8 h-8 text-[#00247D] mx-auto mb-2" />
            <CardTitle className="text-lg">Have a Coupon?</CardTitle>
            <CardDescription>
              Enter your coupon code to unlock Pro features
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="h-12"
                data-testid="coupon-input"
              />
              <Button
                onClick={handleApplyCoupon}
                disabled={applyingCoupon}
                className="h-12 px-6 bg-[#009E49] hover:bg-[#008840]"
                data-testid="apply-coupon-btn"
              >
                {applyingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-12"><PricingFaq /></div>
      </main>

      <CheckoutDialog
        open={!!checkoutPlan}
        productId={checkoutPlan}
        onClose={() => setCheckoutPlan(null)}
        onPayPal={(planId) => setSelectedPlan(planId)}
      />

      {/* Pro celebration modal — pops the moment a coupon activates so the
          user gets the "reward" moment their action deserves, plus a
          direct path to see what just unlocked. */}
      <Dialog open={proCelebration.open} onOpenChange={(o) => !o && setProCelebration({ open: false, mode: 'trial', days: 0, expires: null })}>
        <DialogContent
          className="sm:max-w-lg overflow-hidden border-0 p-0 gap-0 bg-gradient-to-br from-[#00247D] via-[#001A5C] to-[#000F3A] text-white"
          data-testid="pro-celebration-modal">
          {/* Amber accent stripe — echoes the promo card visual identity. */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-400/40 to-transparent rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-rose-500/30 to-transparent rounded-full blur-3xl pointer-events-none" />

          <div className="relative px-6 sm:px-10 pt-10 pb-8 text-center">
            {/* Animated sparkle crown */}
            <div className="relative inline-flex items-center justify-center mb-5">
              <div className="absolute inset-0 rounded-full bg-amber-400/30 blur-2xl animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-2xl flex items-center justify-center">
                <Crown className="w-10 h-10 text-white drop-shadow-md" strokeWidth={2.4} />
              </div>
              <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-amber-200 animate-pulse" />
              <Sparkles className="absolute -bottom-1 -left-3 w-5 h-5 text-amber-300 animate-pulse" style={{ animationDelay: '0.5s' }} />
            </div>

            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed' }}>
              {proCelebration.mode === 'paid' ? 'WELCOME TO PRO!' : "YOU'RE PRO!"}
            </h2>
            <p className="text-amber-300 text-sm font-bold uppercase tracking-[0.25em] mt-1">
              {proCelebration.mode === 'paid' ? 'Subscription Activated' : 'Coupon Activated'}
            </p>
            <p className="text-white/70 text-sm mt-4">
              {proCelebration.mode === 'paid' ? (
                proCelebration.expires ? (
                  <>Thanks for going Pro! Next renewal on <span className="font-bold text-white">{proCelebration.expires.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>.</>
                ) : (
                  <>Thanks for going Pro! Every Pro feature is unlocked on your account.</>
                )
              ) : proCelebration.days > 0 ? (
                <>Your Pro trial is live for <span className="font-bold text-white">{proCelebration.days} day{proCelebration.days !== 1 ? 's' : ''}</span>{proCelebration.expires && (
                  <> · expires <span className="font-bold text-white">{proCelebration.expires.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span></>
                )}</>
              ) : (
                <>Every Pro feature is now unlocked on your account.</>
              )}
            </p>

            {/* Feature callouts — a curated 4-tile grid so the "what did I
                just unlock?" answer is instantly visible without scrolling.
                Icons match the site's existing vocabulary. */}
            <div className="grid grid-cols-2 gap-3 mt-7 text-left">
              {[
                { icon: ImageIcon, label: 'Team & event logos' },
                { icon: BarChart3, label: 'Live stats & analytics' },
                { icon: Users, label: 'Roster editor for every team' },
                { icon: Palette, label: 'Custom brand accent color' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2.5 bg-white/8 border border-white/10 rounded-lg px-3 py-2.5">
                  <div className="w-8 h-8 shrink-0 rounded-md bg-amber-500/25 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-amber-300" />
                  </div>
                  <span className="text-xs sm:text-sm text-white/90 font-medium leading-tight">{label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-2.5 mt-7">
              <Button
                onClick={handleExploreProFeatures}
                className="flex-1 h-12 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-bold shadow-lg shadow-amber-500/30"
                data-testid="explore-pro-features-btn">
                <Trophy className="w-4 h-4 mr-2" />
                Explore Pro features
              </Button>
              <Button
                variant="ghost"
                onClick={() => setProCelebration({ open: false, mode: 'trial', days: 0, expires: null })}
                className="h-12 text-white/60 hover:text-white hover:bg-white/10 sm:w-32"
                data-testid="dismiss-pro-celebration-btn">
                Maybe later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <SiteFooter />
    </div>
  );
}
